import { getContactListDataByPersonId } from '../contacts/mockData'
import { getTeamMemberListDataByPersonId } from '../teamMembers/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import type { IPerson } from '~/types/person/models'
import type { TEntityId, IEntityDataItem } from '~/types/models'

const personListData = undefined as unknown as IPerson[]

export const getPersonListData = getListDataFunctionFactory<IPerson[]>({
  listData: personListData,
  storageKey: 'persons',
  getStartingListData: getStartingPersonListData,
  sortFunction: (a, b) => (a.name.lastName > b.name.lastName ? 1 : -1),
})

export function _getPDataById(id: TEntityId, list: IPerson[]): IPerson {
  if (!list) {
    throw new Error(`_getPDataById returned undefined/null`)
  }
  const p = list.find((person: IPerson) => person.id === id)
  if (!p) {
    throw new Error(`Can't find person '${id}'`)
  }
  return p
}

export async function getPersonDataById(id: TEntityId): Promise<IPerson> {
  const list = await getPersonListData()
  return _getPDataById(id, list)
}

// this function doesn't appear to be used elsewhere.
export async function getPersonAliasDataByPersonId(id: string) {
  const contactsData = await getContactListDataByPersonId(id)
  const teamMemberData = await getTeamMemberListDataByPersonId(id)
  return [...contactsData, ...teamMemberData]
}

export async function getStartingPersonListData(): Promise<IPerson[]> {
  return [
    // Case Bot: the identity Cynque stamps on the rows it creates itself, today
    // the notes and documents extracted from inbound support email.
    //
    // ⚠ Named for the WRITER, not for the company, because more bots are
    // expected -- one per automated writer. That is why nothing keys on a
    // well-known id: `isSystem` is a flag precisely so it can be true of several
    // rows. Do not rewrite this as "the" principal.
    //
    // A TeamMember requires a Person, so a bot needs one even though it is not a
    // person. It is a real row rather than a dangling reference on XXTM0000,
    // because the server's FK makes it real there too -- a mock that skipped it
    // would be narrower than the server in exactly the way that hides defects.
    //
    // ⚠ Filtered out of getOfferablePersons, so nobody can attach a contact, a
    // user or a team member to a bot. It DOES appear on /Persons, deliberately,
    // for the same reason XXTM0000 appears on /Team Members: an author with no
    // row to explain it is worse than a visible oddity.
    //
    // ⚠ `lastName: 'Bot'` is load-bearing, not decoration. Name() composes
    // `descr` as `firstName + ' ' + lastName` and `initials` from the first
    // character of each, so an empty last name renders "Case " with a trailing
    // space and a one-letter avatar.
    {
      id: 'XXPE0000',
      hrid: 'PE0000',
      isSystem: true,
      name: {
        firstName: 'Case',
        lastName: 'Bot',
      },
      phone: '',
      cell: '',
      email: '',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0001',
      hrid: 'PE0001',
      name: {
        firstName: 'Isaac',
        lastName: 'Newton',
      },
      phone: '(777) 123-2342',
      cell: '(777) 999-8888',
      email: 'isaac@newton.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0002',
      hrid: 'PE0002',
      name: {
        firstName: 'Galileo',
        lastName: 'Galilei',
      },
      phone: '(777) 234-3453',
      cell: '(777) 999-8887',
      email: 'galileo@galilei.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0003',
      hrid: 'PE0003',
      name: {
        firstName: 'Albert',
        lastName: 'Einstein',
      },
      phone: '(777) 345-4564',
      cell: '(777) 999-8886',
      email: 'albert@einstein.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0004',
      hrid: 'PE0004',
      name: {
        firstName: 'Richard',
        lastName: 'Feyman',
      },
      phone: '(777) 456-7775',
      cell: '(777) 999-8885',
      email: 'richard@feynman.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0005',
      hrid: 'PE0005',
      name: {
        firstName: 'Samwise',
        lastName: 'Gangees',
      },
      phone: '(777) 123-2342',
      cell: '(777) 999-8884',
      email: 'samwise@gangees.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0006',
      hrid: 'PE0006',
      name: {
        firstName: 'Frodo',
        lastName: 'Baggins',
      },
      phone: '(777) 234-3453',
      cell: '(777) 999-8883',
      email: 'frodo@baggins.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0007',
      hrid: 'PE0007',
      name: {
        firstName: 'Niels',
        lastName: 'Bohr',
      },
      phone: '(777) 345-4564',
      cell: '(777) 999-8882',
      email: 'neils@bohr.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0008',
      hrid: 'PE0008',
      name: {
        firstName: 'Marie',
        lastName: 'Curie',
      },
      phone: '(777) 456-7775',
      cell: '(777) 999-8881',
      email: 'marie@curie.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0009',
      hrid: 'PE0009',
      name: {
        firstName: 'Stephen',
        lastName: 'Hawking',
      },
      phone: '(777) 123-2342',
      cell: '(777) 999-8879',
      email: 'stephen@hawking.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0010',
      hrid: 'PE0010',
      name: {
        firstName: 'Ernest',
        lastName: 'Rutherford',
      },
      phone: '(777) 234-3453',
      cell: '(777) 999-8878',
      email: 'ernest@rutherford.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0011',
      hrid: 'PE0011',
      name: {
        firstName: 'Harry',
        lastName: 'Potter',
      },
      phone: '(777) 345-4564',
      cell: '(777) 999-8877',
      email: 'harry@potter.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0012',
      hrid: 'PE0012',
      name: {
        firstName: 'Hermione',
        lastName: 'Granger',
      },
      phone: '(777) 456-7775',
      cell: '(777) 999-8876',
      email: 'hermione@granger.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0013',
      hrid: 'PE0013',
      name: {
        firstName: 'Michael',
        lastName: 'Faraday',
      },
      phone: '(777) 123-2342',
      cell: '(777) 999-8875',
      email: 'michael@faraday.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0014',
      hrid: 'PE0014',
      name: {
        firstName: 'Paul',
        lastName: 'Dirac',
      },
      phone: '(777) 234-3453',
      cell: '(777) 999-8874',
      email: 'paul@dirac.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0015',
      hrid: 'PE0015',
      name: {
        firstName: 'Edwin',
        lastName: 'Schrodinger',
      },
      phone: '(777) 345-4564',
      cell: '(777) 999-88873',
      email: 'edwin@schrodinger.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0016',
      hrid: 'PE0016',
      name: {
        firstName: 'Max',
        lastName: 'Born',
      },
      phone: '(777) 456-7775',
      cell: '(777) 999-8872',
      email: 'max@born.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0017',
      hrid: 'PE0017',
      name: {
        firstName: 'Gandalf',
        lastName: 'the Great',
      },
      phone: '(777) 234-3453',
      cell: '(777) 999-8871',
      email: 'gandalf@great.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0018',
      hrid: 'PE0018',
      name: {
        firstName: 'Meryl',
        lastName: 'Streep',
      },
      phone: '(510) 343-3234',
      cell: '(777) 999-8860',
      email: 'meryl@streep.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0019',
      hrid: 'PE0019',
      name: {
        firstName: 'James',
        lastName: 'Patterson',
      },
      phone: '(520) 494-4049',
      cell: '(777) 999-8869',
      email: 'james@patterson.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0020',
      hrid: 'PE0020',
      name: {
        firstName: 'Jeffrey',
        lastName: 'Deaver',
      },
      phone: '(510) 234-5656',
      cell: '(777) 999-8868',
      email: 'jeffrey@deaver.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0021',
      hrid: 'PE0021',
      name: {
        firstName: 'Jonathan',
        lastName: 'Kellerman',
      },
      phone: '(310) 123-3230',
      cell: '(777) 999-8867',
      email: 'jonathan@kellerman.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0022',
      hrid: 'PE0022',
      name: {
        firstName: 'Lee',
        lastName: 'Child',
      },
      phone: '(626) 454-1234',
      cell: '(777) 999-8866',
      email: 'lee@child.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0023',
      hrid: 'PE0023',
      name: {
        firstName: 'Robert',
        lastName: 'Parker',
      },
      phone: '(838) 529-1293',
      cell: '(777) 999-8865',
      email: 'robert@parker.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0024',
      hrid: 'PE0024',
      name: {
        firstName: 'Faye',
        lastName: 'Kellerman',
      },
      phone: '(727) 800-1453',
      cell: '(777) 999-8864',
      email: 'faye@kellerman.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0025',
      hrid: 'PE0025',
      name: {
        firstName: 'Ursula',
        lastName: 'LaGuin',
      },
      phone: '(213) 122-0004',
      cell: '(777) 999-8863',
      email: 'ursuls@laguin.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0026',
      hrid: 'PE0026',
      name: {
        firstName: 'Mickey',
        lastName: 'Mantle',
      },
      phone: '(510) 341-7777',
      cell: '(777) 999-8862',
      email: 'mickey@mantle.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0027',
      hrid: 'PE0027',
      name: {
        firstName: 'Babe',
        lastName: 'Ruth',
      },
      phone: '(777) 343-3234',
      cell: '(777) 999-8861',
      email: 'babe@ruth.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0029',
      hrid: 'PE0029',
      name: {
        firstName: 'Pippin',
        lastName: 'Took',
      },
      phone: '(777) 234-1234',
      cell: '(777) 999-8763',
      email: 'pippin@took.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0030',
      hrid: 'PE0030',
      name: {
        firstName: 'Merry',
        lastName: 'Brandybuck',
      },
      phone: '(777) 234-2323',
      cell: '(777) 999-8783',
      email: 'meery@brandybook.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0031',
      hrid: 'PE0031',
      name: {
        firstName: 'Bilbo',
        lastName: 'Baggins',
      },
      phone: '(777) 123-3453',
      cell: '(777) 888-8883',
      email: 'bilbo@baggins.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0032',
      hrid: 'PE0032',
      name: {
        firstName: 'Harlan',
        lastName: 'Ellison',
      },
      phone: '(999) 321-3454',
      cell: '(999) 876-8889',
      email: 'harlan@ellison.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0034',
      hrid: 'PE0034',
      name: {
        firstName: 'House',
        lastName: 'Accounts',
      },
      phone: '',
      email: 'house@accounts.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0035',
      hrid: 'PE0035',
      name: {
        firstName: 'Benedict',
        lastName: 'Cumberbatch',
      },
      phone: '510-842-4444',
      email: 'benedicth@cumberbatch.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0036',
      hrid: 'PE0036',
      name: {
        firstName: 'Scarlett',
        lastName: 'Johansson',
      },
      phone: '510-742-6666',
      email: 'scarlett@johansson.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0037',
      hrid: 'PE0037',
      name: {
        firstName: 'Robert',
        lastName: 'Downy',
        suffix: 'Jr.',
      },
      phone: '510-642-5432',
      email: 'robert@downeyjr.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0038',
      hrid: 'PE0038',
      name: {
        firstName: 'Service',
        lastName: 'Technician',
      },
      phone: '510-111-1000',
      email: 'service@technician.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0039',
      hrid: 'PE0039',
      name: {
        firstName: 'Anthony',
        lastName: 'Hopkins',
      },
      phone: '510-542-1112',
      email: 'anthony@hopkins.org',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0040',
      hrid: 'PE0040',
      name: {
        firstName: 'Jimmy',
        lastName: 'Stewart',
      },
      email: 'jimmy@stewart.com',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0041',
      hrid: 'PE0041',
      name: {
        firstName: 'Rosalyn',
        lastName: 'Russell',
      },
      email: 'rosalyn@russell.com',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0042',
      hrid: 'PE0042',
      name: {
        firstName: 'Robert',
        lastName: 'Mitchum',
      },
      email: 'robert@mitchum.com',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0043',
      hrid: 'PE0043',
      name: {
        firstName: 'Peter',
        lastName: 'Lorre',
      },
      email: 'peter@lorre.com',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0044',
      hrid: 'PE0044',
      name: {
        firstName: 'Peter',
        lastName: 'Lorre',
      },
      email: 'peter@lorre.com',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0045',
      hrid: 'PE0045',
      name: {
        firstName: 'Bill',
        lastName: 'Bones',
      },
      email: 'bill@bones.com',
      status: {
        id: 2,
        label: 'Active',
      },
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXPE0046',
      hrid: 'PE0046',
      name: {
        firstName: 'Viktoria',
        lastName: 'Branz',
        descr: 'Viktoria Branz',
        fullName: 'Viktoria Branz',
        initials: 'VB',
        lastPlusFirst: 'BranzViktoria',
      },
      phone: '(414) 665-8275',
      cell: '(262) 744-9046',
      email: 'viktoriabranz@northwesternmutual.com',
      descr: 'Viktoria Branzn(414) 665-8275',
      label: 'Viktoria Branz',
      status: { id: 2, label: 'Active' },
      createdAt: '2024-01-25T22:00:00.000Z',
      modifiedAt: '2024-01-25T22:00:00.000Z',
      version: '1',
      canBeDeleted: false,
    },
  ]
}
